import React from 'react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { Mail, Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

const ContactPage = () => {
  const { language, t, getLocalizedPath } = useLanguage();

  return (
    <div className="pt-24 pb-20 relative min-h-screen">
      <SEO 
        title={language === 'da' ? "Kontakt Alora Rejser | Personlig rådgivning om handicaprejser" : "Contact Alora Rejser | Personal Advice on Accessible Holidays"} 
        description={language === 'da' ? "Kontakt Alora Rejser for personlig rådgivning om handicapvenlige rejser. Ring på +45 40 50 07 80, send en e-mail til info@alorarejser.dk, eller besøg os i Silkeborg." : "Contact Alora Rejser for personal advice on wheelchair friendly holidays. Call +45 40 50 07 80, email info@alorarejser.dk, or visit us in Silkeborg."}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      {/* Header Section */}
      <section className="py-16 mb-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#99A18C] mb-6">
              {t('ui_contact_us')}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              {language === 'da' ? (
                "Har du spørgsmål til en rejse, eller ønsker du et uforpligtende tilbud på din næste ferie? Vi sidder klar til at hjælpe dig med personlig rådgivning og ro i maven."
              ) : (
                "Do you have questions about a trip, or do you want a free quote for your next holiday? We are ready to help you with personal advice and peace of mind."
              )}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-12 lg:gap-20">
          {/* Contact Information */}
          <div className="space-y-12 py-4">
            <div>
              <h2 className="text-2xl font-serif font-bold text-[#99A18C] mb-6">
                {language === 'da' ? 'Direkte kontakt' : 'Direct Contact'}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#99A18C]/10 p-3 rounded-lg text-[#99A18C] shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#99A18C]">
                      {language === 'da' ? 'Ring til Alora' : 'Call Alora'}
                    </p>
                    <p className="text-slate-600">+45 40 50 07 80</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {language === 'da' ? 'Vi besvarer opkald så hurtigt som muligt.' : 'We answer calls as quickly as possible.'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#99A18C]/10 p-3 rounded-lg text-[#99A18C] shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#99A18C]">
                      {language === 'da' ? 'Send en e-mail' : 'Send an Email'}
                    </p>
                    <p className="text-slate-600">info@alorarejser.dk</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {language === 'da' ? 'Vi svarer altid indenfor 24 timer.' : 'We always reply within 24 hours.'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#99A18C]/10 p-3 rounded-lg text-[#99A18C] shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#99A18C]">
                      {language === 'da' ? 'Adresse' : 'Address'}
                    </p>
                    <p className="text-slate-600">Gjessøvej 56</p>
                    <p className="text-slate-600">8600 Silkeborg</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-stone-100 bg-white/80 backdrop-blur-sm p-5 shadow-sm">
              <p className="text-slate-700 italic leading-relaxed">
                {language === 'da' ? (
                  "Jane er indehaver af Alora Rejser og hjælper med at finde handicapvenlige hoteller og rejser til kørestolsbrugere og deres familier."
                ) : (
                  "Jane is the owner of Alora Rejser and helps find wheelchair friendly hotels and holidays for wheelchair users and their families."
                )}
              </p>
            </div>

            <Link 
              to={getLocalizedPath('practical', language)}
              className="mt-6 block p-6 bg-[#99A18C]/10 rounded-2xl border border-[#99A18C]/20 hover:bg-[#99A18C]/15 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="bg-[#99A18C]/20 p-3 rounded-xl text-[#99A18C] shrink-0 group-hover:bg-[#99A18C]/30 transition-colors">
                  <span className="text-2xl">♿</span>
                </div>
                <div>
                  <h3 className="font-serif font-bold text-[#99A18C] text-lg mb-1 flex items-center gap-2">
                    {language === 'da' ? 'Tilgængelighed, før under og efter din rejse' : 'Accessibility before, during, and after your trip'}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {language === 'da' ? (
                      "Få overblik over praktisk information, flyassistance, hotelstandarder og tryghed på din rejse."
                    ) : (
                      "Get an overview of practical information, flight assistance, hotel standards, and safety on your trip."
                    )}
                  </p>
                </div>
              </div>
            </Link>
          </div>

          {/* Form Section */}
          <div className="bg-white rounded-2xl shadow-xl shadow-stone-200 p-8 border border-stone-100 self-start">
            <div className="mb-8 flex flex-col items-center text-center gap-4">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border border-stone-100 shadow-md">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png"
                  alt="Jane Randløv"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-lg md:text-xl font-semibold text-slate-800">Jane Randløv</p>
                <p className="text-sm text-slate-500">
                  {language === 'da' ? 'Indehaver & rejsekonsulent' : 'Owner & Travel Consultant'}
                </p>
              </div>
            </div>
            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-2xl font-serif font-bold text-[#99A18C] mb-2">
                {language === 'da' ? 'Send en besked' : 'Send a Message'}
              </h2>
              <p className="text-slate-600">
                {language === 'da' ? 'Udfyld formularen, så vender vi tilbage hurtigst muligt.' : 'Fill out the form, and we will get back to you as soon as possible.'}
              </p>
            </div>
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
